import React, { createContext, useContext, useState, useEffect } from 'react';
import { authHelpers, supabase } from '../global/supabase';

// Explicit columns: the PIN hash is never readable by the client.
const USER_PROFILE_COLUMNS =
  'id, email, first_name, last_name, state_license_number, location, is_admin, is_editor, is_active, profile_completed, pin_reset_required';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Computed states
  const isAuthenticated = !!user;
  // needsOnboarding is true only for brand new users who have never seen the welcome screen
  // We check localStorage as a simple flag - if they've seen it once, they never need it again
  const needsOnboarding = isAuthenticated && !authHelpers.hasCompletedOnboarding();
  // needsProfileSetup checks if profile_completed flag is false/null
  const needsProfileSetup = isAuthenticated && userProfile !== null && !userProfile?.profileCompleted;

  // Single session check on mount
  useEffect(() => {
    let mounted = true;
    let hasInitialized = false;

    const loadUserProfile = async (userId) => {
      try {
        console.log('Fetching profile from database for userId:', userId);

        const queryPromise = supabase
          .from('users')
          .select(USER_PROFILE_COLUMNS)
          .eq('id', userId)
          .single();

        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Profile load timeout')), 5000)
        );

        const { data: dbProfile, error } = await Promise.race([queryPromise, timeoutPromise]);

        if (error) {
          console.error('Error loading user profile:', error);
          console.error('Error details:', JSON.stringify(error, null, 2));
          return;
        }

        console.log('DB Profile loaded:', dbProfile);

        if (dbProfile && mounted) {
          setUserProfile({
            id: dbProfile.id,
            email: dbProfile.email,
            firstName: dbProfile.first_name || '',
            lastName: dbProfile.last_name || '',
            stateLicenseNumber: dbProfile.state_license_number || '',
            pinResetRequired: dbProfile.pin_reset_required === true,
            isEditor: dbProfile.is_editor || false,
            isAdmin: dbProfile.is_admin || false,
            editor: dbProfile.is_editor,
            admin: dbProfile.is_admin,
            profileCompleted: dbProfile.profile_completed || false
          });
        }
      } catch (error) {
        // Only log non-timeout errors - timeouts are expected on first auth event
        if (error.message !== 'Profile load timeout') {
          console.error('Error loading user profile:', error);
          console.error('Error stack:', error.stack);
        }
        throw error; // Re-throw so the caller can handle it
      }
    };

    const finishInitialization = () => {
      if (!hasInitialized && mounted) {
        console.log('Setting loading=false, initialized=true');
        hasInitialized = true;
        setLoading(false);
        setInitialized(true);
      }
    };

    // Listen for auth changes - THIS is what actually works!
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state change:', event, session?.user?.email);

      if (!mounted) return;

      // Clear the safety timeout since auth state change fired
      clearTimeout(safetyTimeout);

      try {
        if (session?.user) {
          console.log('Loading profile for user:', session.user.id);
          setUser(session.user);
          await loadUserProfile(session.user.id);
        } else {
          setUser(null);
          setUserProfile(null);
        }
      } catch (error) {
        // Ignore timeout errors on SIGNED_IN event - INITIAL_SESSION will retry
        if (error.message === 'Profile load timeout' && event === 'SIGNED_IN') {
          console.log('Profile load timed out on SIGNED_IN, will retry on INITIAL_SESSION');
        } else {
          console.error('Auth state change error:', error);
        }
      } finally {
        // Initialize after handling auth state
        finishInitialization();
      }
    });

    // Safety timeout: if onAuthStateChange doesn't fire within 2 seconds, finish init anyway
    const safetyTimeout = setTimeout(() => {
      console.log('Safety timeout - finishing initialization');
      finishInitialization();
    }, 2000);

    return () => {
      mounted = false;
      clearTimeout(safetyTimeout);
      subscription.unsubscribe();
    };
  }, []);

  const refreshProfile = async () => {
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser) {
        setUser(currentUser);

        // Fetch user profile from database
        const { data: dbProfile } = await supabase
          .from('users')
          .select(USER_PROFILE_COLUMNS)
          .eq('id', currentUser.id)
          .single();

        if (dbProfile) {
          setUserProfile({
            id: dbProfile.id,
            email: dbProfile.email,
            firstName: dbProfile.first_name || '',
            lastName: dbProfile.last_name || '',
            stateLicenseNumber: dbProfile.state_license_number || '',
            pinResetRequired: dbProfile.pin_reset_required === true,
            isEditor: dbProfile.is_editor || false,
            isAdmin: dbProfile.is_admin || false,
            editor: dbProfile.is_editor,
            admin: dbProfile.is_admin,
            profileCompleted: dbProfile.profile_completed || false
          });
        }
      }
    } catch (error) {
      console.error('Refresh profile error:', error);
    }
  };

  const signOut = async () => {
    console.log('🚪 Signing out...');
    try {
      // Clear local state immediately
      setUser(null);
      setUserProfile(null);

      // Try to sign out from Supabase (with timeout in case it hangs)
      const signOutPromise = authHelpers.signOut();
      const timeoutPromise = new Promise((resolve) => setTimeout(() => {
        console.log('⚠️ Sign out timeout, proceeding anyway');
        resolve({ error: null });
      }, 2000));

      await Promise.race([signOutPromise, timeoutPromise]);
      console.log('✅ Signed out successfully');
    } catch (error) {
      console.error('❌ Sign out error:', error);
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    initialized,
    isAuthenticated,
    needsOnboarding,
    needsProfileSetup,
    refreshProfile,
    signOut
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
